/**
 * Soft-safe Move 3: shared step-panel helpers (widgets, nav, softHint).
 * Soft-refuse: identity lock held (no metric Soft UI copy). Soft 14 ops-only.
 * Soft-safe ①: does not own share payloads — shareViewModel remains sole human face.
 */
import {
  LOCALES,
  t,
} from '../../i18n/strings'
import {
  isExplainMode,
  toggleExplainMode,
} from '../../lib/explainMode'
import {
  boardMismatchWarnings,
  formatSeatFullAccess,
  getSteps,
} from '../../lib/poker'
import {
  REVIEW_SCOPES,
  clampStreetToScope,
  getReviewScope,
  setReviewScope,
  streetOpenForEntry,
} from '../../lib/reviewScope'
import type {
  ReviewScope,
} from '../../lib/reviewScope'
import {
  localGet,
  localSet,
} from '../../lib/storageKeys'
import type {
  StepId,
} from '../../lib/types'
import {
  areBlindsValid,
  getState,
  handLooksIncomplete,
  heroHolesComplete,
  isStepChipInJourney,
  unlockStepsThrough,
  openSoftGate,
  setActionStreet,
  setAppLocale,
  setBlinds,
  setCaptureMode,
  setHeroStackBb,
  setHeroStackChips,
  setPotBb,
  setStep,
  showToast,
} from '../../state'
import {
  el,
} from '../dom'
import {
  clearActiveInfoEdit,
} from '../tableViz'
import type {
  TablePlayInfo,
} from '../tableViz'

export const SEAT_ABBREV_HINT_SUFFIX = 'seat-abbrev-hint-seen'
/** Keep first-visit Seat abbrev hint visible across re-renders this session. */
let seatAbbrevHintVisibleMemory: boolean | null = null

export function shouldShowSeatAbbrevHint(): boolean {
  if (seatAbbrevHintVisibleMemory != null) return seatAbbrevHintVisibleMemory
  if (localGet(SEAT_ABBREV_HINT_SUFFIX) === '1') {
    seatAbbrevHintVisibleMemory = false
    return false
  }
  seatAbbrevHintVisibleMemory = true
  localSet(SEAT_ABBREV_HINT_SUFFIX, '1')
  return true
}

export function toastSeatFullName(pos: string, playerCount: number): void {
  showToast('seatFullName', formatSeatFullAccess(pos, playerCount))
}
export const STACK_BB_STOPS = [
  10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 100, 120, 150, 200, 250, 300,
] as const
export const POT_BB_STOPS = [
  1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50,
] as const

export const STACK_BB_MAJOR = new Set([20, 50, 100, 150, 200, 300])
export const POT_BB_MAJOR = new Set([3, 5, 10, 20, 30, 50])

export function nearestStopIndex(stops: readonly number[], value: number): number {
  let best = 0
  let bestDist = Infinity
  for (let i = 0; i < stops.length; i++) {
    const d = Math.abs(stops[i]! - value)
    if (d < bestDist) {
      bestDist = d
      best = i
    }
  }
  return best
}


export function playInfoFromHand(hand: ReturnType<typeof getState>['hand']): TablePlayInfo {
  const info: TablePlayInfo = {
    smallBlind: hand.smallBlind,
    bigBlind: hand.bigBlind,
    heroStackBb: hand.heroStackBb,
    heroStackChips: hand.heroStackChips,
    stackUnit: hand.stackUnit,
  }
  if (hand.potBb != null && Number.isFinite(hand.potBb)) info.potBb = hand.potBb
  if (hand.effectiveStackBb != null && Number.isFinite(hand.effectiveStackBb)) {
    info.effectiveStackBb = hand.effectiveStackBb
  }
  return info
}

/** Ephemeral table-info editor focus lives in tableViz; bump to re-render. */
export function bumpTableInfoUi(): void {
  setCaptureMode(getState().captureMode)
}

export function tableInfoEditHandlers() {
  return {
    infoEditable: true as const,
    onSetBlinds: (sb: number, bb: number) => setBlinds({ smallBlind: sb, bigBlind: bb }),
    onSetPotBb: (potBb: number | null) => setPotBb(potBb),
    onSetHeroStackBb: (bb: number) => setHeroStackBb(bb),
    onSetHeroStackChips: (chips: number) => setHeroStackChips(chips),
    onInfoEditChange: () => bumpTableInfoUi(),
  }
}

export function formatHintBb(n: number): string {
  const rounded = Math.round(n * 100) / 100
  if (Number.isInteger(rounded)) return String(rounded)
  return String(rounded)
    .replace(/(\.[0-9]*?)0+$/, '$1')
    .replace(/\.$/, '')
}

export function chipButton(opts: {
  label: string
  active: boolean
  onClick: () => void
  title?: string
  tabIndex?: number
}): HTMLElement {
  return el('button', {
    className: ['chip', 'sizePresetChip', opts.active ? 'chipActive' : '']
      .filter(Boolean)
      .join(' '),
    attrs: {
      type: 'button',
      'aria-pressed': opts.active ? 'true' : 'false',
      title: opts.title,
      tabindex: opts.tabIndex,
    },
    text: opts.label,
    on: { click: opts.onClick },
  })
}

export function chipRow(
  values: readonly string[],
  current: string,
  onPick: (v: string) => void,
  opts?: { tabIndex?: number; labelOf?: (v: string) => string },
): HTMLElement {
  return el(
    'div',
    { className: 'sizePresetRow' },
    values.map((v) =>
      chipButton({
        label: opts?.labelOf ? opts.labelOf(v) : v,
        active: current === v,
        onClick: () => onPick(v),
        tabIndex: opts?.tabIndex,
      }),
    ),
  )
}

export function labeledChipSection(
  label: string,
  row: HTMLElement,
): HTMLElement {
  return el('div', { className: 'sizePresetSection' }, [
    el('span', { className: 'sizePresetsLabel', text: label }),
    row,
  ])
}

type SnapSliderOpts = {
  stops: readonly number[]
  /** Current numeric value; null shows dash / first stop without committing. */
  value: number | null
  /** When value is null, show this instead of an em dash (e.g. custom size). */
  displayUnset?: string
  formatPrimary: (n: number) => string
  formatSecondary?: (n: number) => string | null
  onCommit: (n: number) => void
  /** Live update during drag (optional; readout always updates locally). */
  onLive?: (n: number) => void
  tabIndex?: number
  ariaLabel?: string
  hint?: string
  majorStops?: ReadonlySet<number>
  clear?: { label: string; onClear: () => void; active: boolean }
}

/** Half of the visual thumb — ticks & X→index mapping share this inset. */
export const SNAP_THUMB_PAD = 18

/** Map pointer X on the track to the nearest discrete stop index. */
function stopIndexFromClientX(
  clientX: number,
  trackEl: HTMLElement,
  stopCount: number,
): number {
  const rect = trackEl.getBoundingClientRect()
  const n = Math.max(1, stopCount)
  const pad = Math.min(rect.width / 4, SNAP_THUMB_PAD)
  const usable = Math.max(1, rect.width - pad * 2)
  const x = Math.min(usable, Math.max(0, clientX - rect.left - pad))
  const ratio = x / usable
  return Math.min(n - 1, Math.max(0, Math.round(ratio * (n - 1))))
}

/** Touch-friendly discrete snap slider with ± stop steppers and tick marks. */
export function snapSlider(opts: SnapSliderOpts): HTMLElement {
  const stops = opts.stops
  const hasValue = opts.value != null && Number.isFinite(opts.value)
  // Unset → index 0 so thumb isn't parked on a mid-track "ghost" value.
  // Set → nearest stop; readout always mirrors stops[index].
  const idx = hasValue ? nearestStopIndex(stops, opts.value as number) : 0
  const current = stops[idx]!
  const primaryText = hasValue
    ? opts.formatPrimary(current)
    : ((opts.displayUnset ?? '').trim() || '—')

  const primaryEl = el('span', {
    className: 'snapValuePrimary',
    text: primaryText,
  })
  const secondaryText =
    hasValue && opts.formatSecondary ? opts.formatSecondary(current) : null
  const secondaryEl = el('span', {
    className: 'snapValueSecondary',
    text: secondaryText ?? '',
  })
  if (!secondaryText) secondaryEl.hidden = true

  let liveIdx = idx
  let flashTimer: number | undefined

  const updateReadout = (n: number, displayLabel?: string) => {
    const label = displayLabel ?? opts.formatPrimary(n)
    primaryEl.textContent = label
    // Expose the stop number + display units (e.g. 2.5bb), not the range index.
    range.setAttribute('aria-valuenow', String(n))
    range.setAttribute('aria-valuetext', label)
    range.setAttribute('data-stop', label)
    root.setAttribute('data-stop', label)
    if (opts.formatSecondary) {
      const s = opts.formatSecondary(n)
      secondaryEl.textContent = s ?? ''
      secondaryEl.hidden = !s
    }
  }

  const flashReadout = () => {
    primaryEl.classList.remove('snapValueFlash')
    // Force reflow so repeated flashes retrigger the animation.
    void primaryEl.offsetWidth
    primaryEl.classList.add('snapValueFlash')
    if (flashTimer !== undefined) window.clearTimeout(flashTimer)
    flashTimer = window.setTimeout(() => {
      primaryEl.classList.remove('snapValueFlash')
      flashTimer = undefined
    }, 420)
  }

  let range!: HTMLInputElement
  let root!: HTMLElement
  let suppressChange = false
  let gestureStartIdx = idx
  let gestureActive = false
  let pointerId: number | null = null

  const applyIndex = (
    i: number,
    mode: 'live' | 'commit',
    flashOpts?: { flashIfChangedFrom?: number },
  ) => {
    const clamped = Math.min(stops.length - 1, Math.max(0, i))
    const from = flashOpts?.flashIfChangedFrom
    liveIdx = clamped
    range.value = String(clamped)
    const n = stops[clamped]!
    updateReadout(n)
    if (mode === 'live') {
      opts.onLive?.(n)
      return
    }
    if (from != null && from !== clamped) flashReadout()
    opts.onCommit(n)
  }

  range = el('input', {
    className: 'snapSliderInput',
    attrs: {
      type: 'range',
      min: 0,
      max: stops.length - 1,
      step: 1,
      value: idx,
      'aria-label': opts.ariaLabel,
      'aria-valuemin': stops[0]!,
      'aria-valuemax': stops[stops.length - 1]!,
      'aria-valuenow': current,
      'aria-valuetext': primaryText,
      'data-stop': primaryText,
      tabindex: opts.tabIndex,
    },
    on: {
      // Keyboard / AT only — pointer path is driven by the overlay below
      // so UA page-step on track click cannot land on a distant neighbor.
      input: (e) => {
        if (gestureActive) return
        const i = Number((e.target as HTMLInputElement).value)
        applyIndex(i, 'live')
      },
      change: () => {
        if (suppressChange) {
          suppressChange = false
          return
        }
        if (gestureActive) return
        applyIndex(Number(range.value), 'commit')
      },
    },
  }) as HTMLInputElement
  // Property sync: content-attribute `value` alone can leave a stale IDL
  // value after type=range sanitization; keep thumb/index == readout.
  range.value = String(idx)

  const stepBy = (dir: -1 | 1) => {
    const cur = Number(range.value)
    const next = Math.min(stops.length - 1, Math.max(0, cur + dir))
    if (next === cur) return
    applyIndex(next, 'commit', { flashIfChangedFrom: cur })
  }

  const ticks = el(
    'div',
    {
      className: 'snapTicks',
      attrs: { 'aria-hidden': 'true' },
    },
    stops.map((n) =>
      el('span', {
        className: [
          'snapTick',
          opts.majorStops?.has(n) ? 'snapTickMajor' : '',
        ]
          .filter(Boolean)
          .join(' '),
      }),
    ),
  )

  // Transparent overlay owns pointer targeting (X → nearest stop index).
  // Native range stays for visuals + keyboard; pointer-events none on input.
  const overlay = el('div', {
    className: 'snapSliderOverlay',
    attrs: { 'aria-hidden': 'true' },
  })

  const hit = el('div', { className: 'snapSliderHit' }, [range, overlay])
  const track = el('div', { className: 'snapSliderTrack' }, [hit, ticks])

  const indexFromEvent = (e: PointerEvent) =>
    stopIndexFromClientX(e.clientX, hit, stops.length)

  const endGesture = () => {
    gestureActive = false
    pointerId = null
  }

  overlay.addEventListener('pointerdown', (e) => {
    if (e.button != null && e.button !== 0) return
    e.preventDefault()
    gestureActive = true
    gestureStartIdx = liveIdx
    pointerId = e.pointerId
    try {
      overlay.setPointerCapture(e.pointerId)
    } catch {
      // ignore — capture optional
    }
    applyIndex(indexFromEvent(e), 'live')
  })
  overlay.addEventListener('pointermove', (e) => {
    if (!gestureActive) return
    if (pointerId != null && e.pointerId !== pointerId) return
    applyIndex(indexFromEvent(e), 'live')
  })
  const finishPointer = (e: PointerEvent) => {
    if (!gestureActive) return
    if (pointerId != null && e.pointerId !== pointerId) return
    const from = gestureStartIdx
    const i = indexFromEvent(e)
    endGesture()
    suppressChange = true
    applyIndex(i, 'commit', { flashIfChangedFrom: from })
  }
  overlay.addEventListener('pointerup', finishPointer)
  overlay.addEventListener('pointercancel', () => {
    endGesture()
  })

  const kids: HTMLElement[] = [
    el('div', { className: 'snapValueRow' }, [
      el('button', {
        className: 'snapStepBtn',
        attrs: {
          type: 'button',
          'aria-label': '−',
          tabindex: opts.tabIndex,
        },
        text: '−',
        on: { click: () => stepBy(-1) },
      }),
      el('div', { className: 'snapValue' }, [primaryEl, secondaryEl]),
      el('button', {
        className: 'snapStepBtn',
        attrs: {
          type: 'button',
          'aria-label': '+',
          tabindex: opts.tabIndex,
        },
        text: '+',
        on: { click: () => stepBy(1) },
      }),
    ]),
    track,
  ]

  if (opts.hint) {
    kids.push(el('p', { className: 'muted snapHint', text: opts.hint }))
  }
  if (opts.clear) {
    kids.push(
      el('div', { className: 'snapClearRow' }, [
        chipButton({
          label: opts.clear.label,
          active: opts.clear.active,
          onClick: opts.clear.onClear,
          tabIndex: opts.tabIndex,
        }),
      ]),
    )
  }

  root = el('div', {
    className: 'snapSlider',
    attrs: { 'data-stop': primaryText },
  }, kids)
  return root
}

export function customDetails(
  summary: string,
  input: HTMLElement,
  open?: boolean,
  onToggle?: (open: boolean) => void,
): HTMLElement {
  const details = el('details', {
    className: 'customDetails',
    attrs: open ? { open: '' } : undefined,
  })
  if (onToggle) {
    details.addEventListener('toggle', () => {
      onToggle(details.open)
    })
  }
  details.append(
    el('summary', { className: 'customSummary', text: summary }),
    el('div', { className: 'customBody' }, [input]),
  )
  return details
}

export function requestStep(next: StepId, opts: { card?: boolean } = {}): void {
  const { hand, step } = getState()
  if (step === 'setup' && next !== 'setup' && !areBlindsValid(hand)) {
    return
  }
  // Soft stepwise M2: only current, visited, and immediate +1 (Quiet journey)
  if (!isStepChipInJourney(next)) {
    return
  }
  const steps = getSteps()
  const curIdx = steps.findIndex((s) => s.id === step)
  const nextIdx = steps.findIndex((s) => s.id === next)
  const cardsIdx = steps.findIndex((s) => s.id === 'cards')
  // Soft stepwise M3: forward past cards requires hero holes (board stays optional after)
  if (
    nextIdx > curIdx &&
    nextIdx > cardsIdx &&
    curIdx <= cardsIdx &&
    !heroHolesComplete(hand)
  ) {
    return
  }
  if (next === 'summary' && handLooksIncomplete(hand)) {
    openSoftGate('summary')
    return
  }
  if (next !== step) clearActiveInfoEdit()
  setStep(next, opts)
}

export function go(dir: -1 | 1): void {
  const steps = getSteps()
  const { step } = getState()
  const stepIndex = steps.findIndex((s) => s.id === step)
  const next = steps[stepIndex + dir]
  if (next) requestStep(next.id)
}

/** Quick path: setup → cards (or actions if hole already filled). */
export function quickHandJump(): void {
  const { hand } = getState()
  if (!areBlindsValid(hand)) return
  const holeDone = Boolean(hand.holeCards[0] && hand.holeCards[1])
  const target = holeDone ? 'actions' : 'cards'
  // Soft stepwise M2: Quiet/primary skip unlocks journey through the target
  unlockStepsThrough(target)
  requestStep(target)
}

export const POT_QUICK_BB = [3, 5, 6, 8, 10, 12] as const

export function jumpLink(label: string, onClick: () => void): HTMLElement {
  return el('button', {
    className: 'linkBtn jumpLink',
    attrs: { type: 'button' },
    text: label,
    on: { click: onClick },
  })
}

export function blindsError(): string | null {
  const { hand } = getState()
  if (areBlindsValid(hand)) return null
  return t().errorBlinds
}

/** Empty → null (clear optional); invalid → null; else number. */
export function parseOptionalNumber(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 0) return null
  return n
}


/** Wave-1: pedagogy chrome only when explainMode ON. Honesty softHint stays ungated. */
export function explainOnly(node: HTMLElement | null): HTMLElement | null {
  return isExplainMode() ? node : null
}


/**
 * Soft-outside 2.2 Quiet friction: Review-through stays same-place one layer
 * (<details>), closed by default on full journey (to-river). Soft stepwise M1
 * defaults unset scope to preflop so details auto-open (Soft #4 discovery) and
 * later street pills stay disabled until widened. Never a new tab/section.
 */
let reviewScopeDetailsOpen: boolean | null = null

export function reviewScopeControls(): HTMLElement {
  const ui = t()
  const scope = getReviewScope()
  const { hand, actionStreet } = getState()
  const labelFor = (s: ReviewScope): string =>
    s === 'preflop'
      ? ui.reviewScopePreflop
      : s === 'to-flop'
        ? ui.reviewScopeFlop
        : ui.reviewScopeRiver

  // Soft 2.2: default closed on full journey; auto-open when narrowed (Soft #4 discovery)
  const open =
    reviewScopeDetailsOpen === true ||
    (reviewScopeDetailsOpen !== false && scope !== 'to-river')

  const details = el('details', {
    className: 'reviewScopeChrome',
    attrs: open ? { open: '' } : undefined,
  })
  details.addEventListener('toggle', () => {
    reviewScopeDetailsOpen = (details as HTMLDetailsElement).open
  })
  details.append(
    el('summary', {
      className: 'reviewScopeSummary',
      text: ui.reviewScopeLabel,
    }),
    el(
      'div',
      {
        className: 'reviewScopeRow',
        attrs: { role: 'group', 'aria-label': ui.reviewScopeAria },
      },
      [
        el(
          'div',
          { className: 'reviewScopeChips' },
          REVIEW_SCOPES.map((s) =>
            el('button', {
              className: ['pill', 'reviewScopeChip', scope === s ? 'pillActive' : '']
                .filter(Boolean)
                .join(' '),
              attrs: {
                type: 'button',
                'aria-pressed': scope === s ? 'true' : 'false',
              },
              text: labelFor(s),
              on: {
                click: () => {
                  setReviewScope(s)
                  const count = hand.actions.filter((a) => a.street === actionStreet).length
                  if (!streetOpenForEntry(actionStreet, count > 0, s)) {
                    setActionStreet(clampStreetToScope(actionStreet, s))
                  }
                },
              },
            }),
          ),
        ),
      ],
    ),
  )
  return details
}

export function explainModeToggle(): HTMLElement {
  const ui = t()
  const on = isExplainMode()
  return el('button', {
    className: [
      'btn',
      'btnQuiet',
      'btnCompact',
      'explainModeToggle',
      on ? 'explainModeToggleOn' : '',
    ]
      .filter(Boolean)
      .join(' '),
    attrs: {
      type: 'button',
      'aria-pressed': on ? 'true' : 'false',
      'aria-label': ui.explainModeAria,
      title: ui.explainModeAria,
    },
    text: ui.explainModeLabel,
    on: { click: () => toggleExplainMode() },
  })
}

export function softHint(step: StepId): string | null {
  const ui = t()
  const { hand } = getState()
  // Softwise M10c: no `0/2` counter on the cards step — the title and the
  // picked-card slots already say what is chosen.
  if (step === 'board') {
    const flopCount = hand.board.flop.filter(Boolean).length
    if (flopCount > 0 && flopCount < 3) return ui.hintFlopPartial
  }
  if (step === 'actions') {
    const boardWarns = boardMismatchWarnings(hand)
    if (boardWarns.length) return boardWarns.join(' · ')
    if (hand.actions.length === 0) return ui.hintNoActions
  }
  return null
}

export function languageSwitcher(mode: 'full' | 'compact' = 'full'): HTMLElement {
  const ui = t()
  const { locale } = getState()
  const compact = mode === 'compact'
  const select = el('select', {
    className: ['langSelect', compact ? 'langSelectCompact' : '']
      .filter(Boolean)
      .join(' '),
    attrs: {
      'aria-label': compact ? ui.langCompactAria : ui.langAria,
      title: ui.langAria,
    },
    on: {
      change: (e) => {
        const value = (e.target as HTMLSelectElement).value
        const item = LOCALES.find((l) => l.id === value)
        if (item) setAppLocale(item.id)
      },
    },
  })
  for (const item of LOCALES) {
    const opt = document.createElement('option')
    opt.value = item.id
    // Short codes keep the control narrow on ~390; full name stays in title.
    opt.textContent = item.short
    opt.title = item.label
    opt.lang = item.id
    if (item.id === locale) opt.selected = true
    select.append(opt)
  }
  return el(
    'div',
    {
      className: ['langSwitch', compact ? 'langSwitchCompact' : '']
        .filter(Boolean)
        .join(' '),
      attrs: { role: 'group', 'aria-label': compact ? ui.langCompactAria : ui.langAria },
    },
    [select],
  )
}
